import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import PasswordStrengthBar from 'react-password-strength-bar';

/* https://github.com/lannex/react-password-strength-bar */

const propTypes = {
  password: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  minLength: PropTypes.number,
  scoreWords: PropTypes.array,
  shortScoreWords: PropTypes.string,
  barColors: PropTypes.array,
  showRequirements: PropTypes.bool,
  onValidityChange: PropTypes.func,
  repeatPassword: PropTypes.string
};

const defaultProps = {
  minLength: 8,
  scoreWords: [
    'password_strength_weak', // common and extremely bad passwords
    'password_strength_weak',
    'password_strength_okay',
    'password_strength_good',
    'password_strength_strong'
  ],
  shortScoreWord: 'password_strength_too_short',
  barColors: ['#EEE', '#f2453d', '#f17725', '#0091ec', '#8dc252'], //colors from _variables.css
  showRequirements: false,
  onValidityChange: null,
  repeatPassword: ''
};

const PasswordStrengthMeter = props => {
  const {
    password,
    translate,
    minLength,
    scoreWords,
    shortScoreWord,
    barColors,
    showRequirements,
    onValidityChange,
    repeatPassword,
    showMeter
  } = props;

  const translated = scoreWords.map(key => translate(key));

  // Password requirements validation
  const requirements = [
    {
      key: 'min_length',
      label: translate('password_requirement_min_length'),
      test: pass => pass.length >= minLength
    },
    {
      key: 'uppercase',
      label: translate('password_requirement_uppercase'),
      test: pass => /[A-Z]/.test(pass)
    },
    {
      key: 'lowercase',
      label: translate('password_requirement_lowercase'),
      test: pass => /[a-z]/.test(pass)
    },
    {
      key: 'number',
      label: translate('password_requirement_number'),
      test: pass => /[0-9]/.test(pass)
    },
    {
      key: 'special',
      label: translate('password_requirement_special'),
      test: pass => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(pass)
    },
    {
      key: 'passwords_match',
      label: translate('password_requirement_passwords_match'),
      test: pass => pass.length > 0 && pass === repeatPassword
    }
  ];

  const isTouched = password.length > 0;
  const isValid = requirements.every(req => req.test(password));

  // Notify parent component about validity change
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid, onValidityChange]);

  const getRequirementClass = requirement => {
    if (!isTouched) {
      return 'requirement-untouched';
    }
    return requirement.test(password)
      ? 'requirement-valid'
      : 'requirement-invalid';
  };

  const getRequirementIcon = requirement => {
    if (!isTouched) {
      return null;
    }
    return requirement.test(password) ? (
      <i className="fa fa-check" />
    ) : (
      <i className="fa fa-times" />
    );
  };

  return (
    <>
      {showMeter && (
        <PasswordStrengthBar
          password={password}
          minLength={minLength}
          shortScoreWord={translate(shortScoreWord)}
          scoreWords={translated}
          barColors={barColors}
        />
      )}
      {showRequirements && (
        <div className="password-requirements">
          <ul className="password-requirements-list">
            {requirements.map(requirement => (
              <li
                key={requirement.key}
                className={`password-requirement-item ${getRequirementClass(
                  requirement
                )}`}
              >
                {getRequirementIcon(requirement)}
                {requirement.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

PasswordStrengthMeter.propTypes = propTypes;
PasswordStrengthMeter.defaultProps = defaultProps;

export default PasswordStrengthMeter;
